import type { ModuleManifest } from "../contracts/types";

export type ValidationReport = {
  errors: string[];
  unreachable: string[];
  deadEnds: string[];
};

export function validateModule(module: ModuleManifest): string[] {
  return buildValidationReport(module).errors;
}

export function validateRoutes(module: ModuleManifest): string[] {
  const ids = new Set(module.scenes.map((s) => s.id));
  const errors: string[] = [];
  for (const [from, to] of module.routes ?? []) {
    if (!ids.has(from) || !ids.has(to))
      errors.push(`Invalid route ${from} -> ${to}`);
  }
  for (const scene of module.scenes) {
    for (const action of scene.actions ?? []) {
      for (const target of [
        action.target,
        action.successTarget,
        action.failureTarget,
      ].filter(Boolean) as string[]) {
        if (!ids.has(target))
          errors.push(
            `${scene.id}/${action.id} targets missing scene ${target}`,
          );
      }
    }
  }
  return errors;
}

export function detectUnreachableScenes(module: ModuleManifest): string[] {
  const ids = new Set(module.scenes.map((s) => s.id));
  if (!ids.has(module.startSceneId)) return [...ids];
  const graph = new Map<string, Set<string>>();
  for (const id of ids) graph.set(id, new Set());
  for (const [from, to] of module.routes ?? []) {
    graph.get(from)?.add(to);
  }
  for (const scene of module.scenes) {
    for (const action of scene.actions ?? []) {
      for (const target of [
        action.target,
        action.successTarget,
        action.failureTarget,
      ].filter(Boolean) as string[]) {
        graph.get(scene.id)?.add(target);
      }
    }
  }
  const seen = new Set<string>();
  const queue = [module.startSceneId];
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const next of graph.get(id) ?? []) queue.push(next);
  }
  return [...ids].filter((id) => !seen.has(id));
}

/** Scenes with no outgoing actions and not marked ENDING (excluding intentional terminals). */
export function detectDeadEnds(module: ModuleManifest): string[] {
  return module.scenes
    .filter(
      (scene) =>
        scene.type !== "ENDING" &&
        (scene.actions?.length ?? 0) === 0 &&
        !scene.ending,
    )
    .map((scene) => scene.id);
}

export function buildValidationReport(
  module: ModuleManifest,
): ValidationReport {
  const errors: string[] = [];
  const ids = module.scenes.map((s) => s.id);
  const unique = new Set(ids);
  if (!unique.has(module.startSceneId))
    errors.push(`Missing start scene: ${module.startSceneId}`);
  if (unique.size !== ids.length) errors.push("Duplicate scene ids detected");
  errors.push(...validateRoutes(module));
  const unreachable = detectUnreachableScenes(module);
  if (unreachable.length)
    errors.push(`Unreachable scenes: ${unreachable.join(", ")}`);
  const deadEnds = detectDeadEnds(module);
  return { errors, unreachable, deadEnds };
}
